import { useState, useEffect, FC } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Input } from '../../ui/Input'
import { Select } from '../../ui/Select'
import { Button } from '../../ui/Button'
import postalCodeService, {
  PostalCodeResponse,
} from '../../../services/postalCodeService'
import companyService from '../../../services/companyService'
import { authService } from '../../../services/authService'
import toast from 'react-hot-toast'
import apiClient from '../../../services/api'
import ContinueLaterModal from '../ContinueLaterModal'

interface CompanyDataFormValues {
  companyName: string
  rfc: string
  socialReason: string
  email: string
  phone: string
  website: string
  street: string
  exteriorNumber: string
  interiorNumber: string
  betweenStreets: string
  postalCode: string
  colony: string
  city: string
  municipality: string
  state: string
  stateAbbreviation: string
  operativeSameAsFiscalAddress: boolean
  operativeStreet: string
  operativeExteriorNumber: string
  operativeInteriorNumber: string
  operativeBetweenStreets: string
  operativePostalCode: string
  operativeColony: string
  operativeCity: string
  operativeMunicipality: string
  operativeState: string
  operativeStateAbbreviation: string
}

interface CompanyDataStepProps {
  onComplete?: (stepData: any) => Promise<void>
  stepData?: any
  stepKey?: string
}

const validationSchema = Yup.object({
  companyName: Yup.string().required('El nombre de la empresa es requerido'),
  rfc: Yup.string()
    .transform(value => (value ? value.toUpperCase() : value))
    .matches(
      /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/,
      'El formato del RFC no es válido'
    )
    .required('El RFC es requerido'),
  socialReason: Yup.string().required('La razón social es requerida'),
  email: Yup.string()
    .email('El formato del correo electrónico no es válido')
    .required('El correo electrónico es requerido'),
  phone: Yup.string()
    .matches(/^\d{10}$/, 'El teléfono debe tener exactamente 10 dígitos')
    .required('El teléfono es requerido'),
  website: Yup.string().url('El formato de la URL no es válido'),
  street: Yup.string().required('La calle es requerida'),
  exteriorNumber: Yup.string().required('El número exterior es requerido'),
  postalCode: Yup.string()
    .matches(/^\d{5}$/, 'El código postal debe tener 5 dígitos')
    .required('El código postal es requerido'),
  colony: Yup.string().required('La colonia es requerida'),
  city: Yup.string().required('La ciudad es requerida'),
  state: Yup.string().required('El estado es requerido'),
  operativeStreet: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema.required('La calle del domicilio operativo es requerida'),
    otherwise: schema => schema,
  }),
  operativeExteriorNumber: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema.required(
        'El número exterior del domicilio operativo es requerido'
      ),
    otherwise: schema => schema,
  }),
  operativePostalCode: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema
        .matches(
          /^\d{5}$/,
          'El código postal del domicilio operativo debe tener 5 dígitos'
        )
        .required('El código postal del domicilio operativo es requerido'),
    otherwise: schema => schema,
  }),
  operativeColony: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema.required('La colonia del domicilio operativo es requerida'),
    otherwise: schema => schema,
  }),
  operativeCity: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema.required('La ciudad del domicilio operativo es requerida'),
    otherwise: schema => schema,
  }),
  operativeState: Yup.string().when('operativeSameAsFiscalAddress', {
    is: false,
    then: schema =>
      schema.required('El estado del domicilio operativo es requerido'),
    otherwise: schema => schema,
  }),
})

const handlePhoneChange = (value: string, setFieldValue: any) => {
  // Remove all non-digit characters and limit to 10 digits
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  setFieldValue('phone', digitsOnly)
}

export const CompanyDataStep: FC<CompanyDataStepProps> = ({ onComplete }) => {
  const [loadingPostalCode, setLoadingPostalCode] = useState(false)
  const [loadingOperativePostalCode, setLoadingOperativePostalCode] =
    useState(false)
  const [colonies, setColonies] = useState<{ value: string; label: string }[]>(
    []
  )
  const [operativeColonies, setOperativeColonies] = useState<
    { value: string; label: string }[]
  >([])
  const [postalCodeData, setPostalCodeData] =
    useState<PostalCodeResponse | null>(null)
  const [operativePostalCodeData, setOperativePostalCodeData] =
    useState<PostalCodeResponse | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [initialValues, setInitialValues] = useState<CompanyDataFormValues>({
    companyName: '',
    rfc: '',
    socialReason: '',
    email: '',
    phone: '',
    website: '',
    street: '',
    exteriorNumber: '',
    interiorNumber: '',
    betweenStreets: '',
    postalCode: '',
    colony: '',
    city: '',
    municipality: '',
    state: '',
    stateAbbreviation: '',
    operativeSameAsFiscalAddress: false,
    operativeStreet: '',
    operativeExteriorNumber: '',
    operativeInteriorNumber: '',
    operativeBetweenStreets: '',
    operativePostalCode: '',
    operativeColony: '',
    operativeCity: '',
    operativeMunicipality: '',
    operativeState: '',
    operativeStateAbbreviation: '',
  })

  // Load existing company data on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setIsLoadingData(true)
        console.log('Loading existing company data...')

        // First, get RFC and email from localStorage
        const getStorageData = () => {
          try {
            const userData = localStorage.getItem('user_data')
            if (userData) {
              const user = JSON.parse(userData)
              return {
                rfc: user.rfc,
                name: user.name,
              }
            }
          } catch (error) {
            console.error('Error getting localStorage data:', error)
          }
          return { rfc: null, name: null }
        }

        const { rfc: storageRfc, name: storageName } = getStorageData()
        console.log('📦 Data from localStorage - RFC:', storageRfc)

        // Get existing companies for the user
        const existingCompanies = await companyService.getCompanies()
        console.log('API Response:', existingCompanies)

        let newInitialValues = { ...initialValues }

        // Set RFC from localStorage
        if (storageRfc) {
          newInitialValues.rfc = storageRfc.toUpperCase()
        }

        if (storageName) {
          newInitialValues.companyName = storageName
        }

        // If we have existing company data, populate the form
        if (existingCompanies.length > 0) {
          const company = existingCompanies[0]
          console.log('Found existing company data:', company)

          const fiscalAddress = company.addresses?.find(
            a => a.type === 'COMPANY'
          )
          const operativeAddress = company.addresses?.find(
            a => a.type === 'OPERATIVE'
          )

          newInitialValues = {
            companyName: company.companyName || '',
            rfc: company.rfc || storageRfc?.toUpperCase() || '',
            socialReason: company.businessName || '',
            email: company.email || '',
            phone: company.phoneNumber || '',
            website: company.website || '',
            street: fiscalAddress?.street || '',
            exteriorNumber: fiscalAddress?.exteriorNumber || '',
            interiorNumber: fiscalAddress?.interiorNumber || '',
            betweenStreets: fiscalAddress?.betweenStreets || '',
            postalCode: fiscalAddress?.postalCode || '',
            colony: fiscalAddress?.neighborhood || '',
            city: fiscalAddress?.city || '',
            municipality: fiscalAddress?.municipality || '',
            state: fiscalAddress?.state || '',
            stateAbbreviation: fiscalAddress?.stateAbbreviation || '',
            operativeSameAsFiscalAddress:
              company.operativeSameAsFiscalAddress || false,
            operativeStreet: operativeAddress?.street || '',
            operativeExteriorNumber: operativeAddress?.exteriorNumber || '',
            operativeInteriorNumber: operativeAddress?.interiorNumber || '',
            operativeBetweenStreets: operativeAddress?.betweenStreets || '',
            operativePostalCode: operativeAddress?.postalCode || '',
            operativeColony: operativeAddress?.neighborhood || '',
            operativeCity: operativeAddress?.city || '',
            operativeMunicipality: operativeAddress?.municipality || '',
            operativeState: operativeAddress?.state || '',
            operativeStateAbbreviation:
              operativeAddress?.stateAbbreviation || '',
          }

          // Re-hidratar el <Select> de Colonia para ambos domicilios;
          // sin esto quedaría deshabilitado aunque el campo ya tenga valor.
          if (fiscalAddress?.postalCode) {
            try {
              const postalData = await postalCodeService.searchByPostalCode(
                fiscalAddress.postalCode
              )
              if (postalData) {
                setPostalCodeData(postalData)
                setColonies(
                  postalData.colonias?.map((col: string) => ({
                    value: col,
                    label: postalCodeService.formatColonyName(col),
                  })) || []
                )
              }
            } catch (error) {
              console.error('Error loading fiscal postal code data:', error)
            }
          }

          if (operativeAddress?.postalCode) {
            try {
              const operativePostalData =
                await postalCodeService.searchByPostalCode(
                  operativeAddress.postalCode
                )
              if (operativePostalData) {
                setOperativePostalCodeData(operativePostalData)
                setOperativeColonies(
                  operativePostalData.colonias?.map((col: string) => ({
                    value: col,
                    label: postalCodeService.formatColonyName(col),
                  })) || []
                )
              }
            } catch (error) {
              console.error(
                'Error loading operative postal code data:',
                error
              )
            }
          }

          toast.success('Datos existentes cargados')
        } else {
          // No existing data, just set RFC from localStorage
          newInitialValues.rfc = storageRfc?.toUpperCase() || ''
        }

        setInitialValues(newInitialValues)
      } catch (error) {
        console.error('Error loading existing data:', error)
        // Still set RFC from localStorage even if API fails
        const getStorageData = () => {
          try {
            const userData = localStorage.getItem('user_data')
            if (userData) {
              const user = JSON.parse(userData)
              return user.rfc
            }
          } catch (error) {
            console.error('Error getting localStorage data:', error)
          }
          return null
        }

        const storageRfc = getStorageData()
        if (storageRfc) {
          setInitialValues(prev => ({ ...prev, rfc: storageRfc.toUpperCase() }))
        }
      } finally {
        setIsLoadingData(false)
      }
    }

    loadExistingData()
  }, [])

  // Handle postal code lookup
  const handlePostalCodeChange = async (
    value: string,
    setFieldValue: any,
    fieldPrefix = ''
  ) => {
    const postalCodeField = fieldPrefix
      ? `${fieldPrefix}PostalCode`
      : 'postalCode'
    setFieldValue(postalCodeField, value)

    const isOperative = fieldPrefix === 'operative'
    const setLoading = isOperative
      ? setLoadingOperativePostalCode
      : setLoadingPostalCode
    const setPostalData = isOperative
      ? setOperativePostalCodeData
      : setPostalCodeData
    const setColoniesData = isOperative ? setOperativeColonies : setColonies

    // Clear previous data when changing postal code
    if (value.length !== 5) {
      setPostalData(null)
      setColoniesData([])
      setFieldValue(`${fieldPrefix}${fieldPrefix ? 'C' : 'c'}olony`, '')
      setFieldValue(`${fieldPrefix}${fieldPrefix ? 'C' : 'c'}ity`, '')
      setFieldValue(`${fieldPrefix}${fieldPrefix ? 'M' : 'm'}unicipality`, '')
      setFieldValue(`${fieldPrefix}${fieldPrefix ? 'S' : 's'}tate`, '')
      setFieldValue(
        `${fieldPrefix}${fieldPrefix ? 'S' : 's'}tateAbbreviation`,
        ''
      )
      return
    }

    // Fetch postal code data when 5 digits are entered
    if (value.length === 5 && /^\d{5}$/.test(value)) {
      setLoading(true)
      try {
        const data = await postalCodeService.searchByPostalCode(value)

        if (data && data.valid) {
          setPostalData(data)

          // Format and set colonies
          const formattedColonies = data.colonias.map((col: string) => ({
            value: col,
            label: postalCodeService.formatColonyName(col),
          }))
          setColoniesData(formattedColonies)

          // Auto-fill state, city, municipality and abbreviation with correct field names
          const stateField = fieldPrefix ? `${fieldPrefix}State` : 'state'
          const cityField = fieldPrefix ? `${fieldPrefix}City` : 'city'
          const municipalityField = fieldPrefix
            ? `${fieldPrefix}Municipality`
            : 'municipality'
          const stateAbbreviationField = fieldPrefix
            ? `${fieldPrefix}StateAbbreviation`
            : 'stateAbbreviation'
          const colonyField = fieldPrefix ? `${fieldPrefix}Colony` : 'colony'

          setFieldValue(
            stateField,
            postalCodeService.formatStateName(data.estado)
          )
          setFieldValue(
            cityField,
            postalCodeService.formatCityName(data.ciudad || data.municipio)
          )
          setFieldValue(municipalityField, data.municipio)
          setFieldValue(stateAbbreviationField, data.abreviatura)
          setFieldValue(
            colonyField,
            formattedColonies.length === 1 ? formattedColonies[0].value : ''
          )
        } else {
          toast.error('Código postal no encontrado')
          setPostalData(null)
          setColoniesData([])
        }
      } catch (error) {
        console.error('Error fetching postal code:', error)
        toast.error('Error al buscar el código postal')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSameAsFiscalChange = (
    checked: boolean,
    values: CompanyDataFormValues,
    setFieldValue: any
  ) => {
    setFieldValue('operativeSameAsFiscalAddress', checked)

    if (checked) {
      // Copy fiscal address data to operative address
      setFieldValue('operativeStreet', values.street)
      setFieldValue('operativeExteriorNumber', values.exteriorNumber)
      setFieldValue('operativeInteriorNumber', values.interiorNumber)
      setFieldValue('operativeBetweenStreets', values.betweenStreets)
      setFieldValue('operativePostalCode', values.postalCode)
      setFieldValue('operativeColony', values.colony)
      setFieldValue('operativeCity', values.city)
      setFieldValue('operativeMunicipality', values.municipality)
      setFieldValue('operativeState', values.state)
      setFieldValue('operativeStateAbbreviation', values.stateAbbreviation)

      // Copy colonies data
      setOperativeColonies(colonies)
      setOperativePostalCodeData(postalCodeData)
    }
  }

  const handleFormSubmit = async (values: CompanyDataFormValues) => {
    try {
      console.log('Form data to submit:', values)

      // Save company data (create or update)
      const creditAppId = await authService.getCurrentCreditApplicationId()
      const savedCompany = await companyService.saveCompanyData(
        values,
        creditAppId
      )

      console.log('Company saved successfully:', savedCompany)
      toast.success('Datos de la empresa guardados correctamente')

      // Actualizar estatus a 'Buro Empresa'
      try {
        const prospectoQuoteId = localStorage.getItem('prospectoQuoteId')
        const token = localStorage.getItem('auth_token')
        if (prospectoQuoteId && token) {
          await apiClient.patch(
            `/cotizador/mis-cotizaciones-prospecto/${prospectoQuoteId}/estatus`,
            { estatus: 'Buro Empresa' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          console.log('✅ Estatus "Buro Empresa" actualizado exitosamente')
        }
      } catch (estatusError) {
        console.error(
          '❌ Error actualizando estatus "Buro Empresa":',
          estatusError
        )
      }

      // Call onComplete if provided
      if (onComplete) {
        await onComplete(values)
      }
    } catch (error: unknown) {
      console.error('Error saving company data:', error)

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } }
        }
        if (axiosError.response?.data?.message) {
          toast.error(axiosError.response.data.message)
        } else {
          toast.error('Error al guardar los datos de la empresa')
        }
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Error al guardar los datos de la empresa')
      }
    }
  }

  // Show loading screen while loading initial data
  if (isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-h3 font-bold text-gray-900 mb-4">
          Datos de la empresa
        </h1>
        <p className="text-gray-600 text-paragraph-2 max-w-3xl mx-auto">
          Completa la información general de la empresa para continuar con el
          proceso de arrendamiento. Asegúrate de que los datos coincidan con los
          documentos oficiales.
        </p>
        <p className="text-gray-600 text-paragraph-2 max-w-3xl mx-auto mt-2">
          Recuerda, esta información se utilizará para realizar la consulta a
          Buró de Crédito.
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
          handleBlur,
        }) => (
          <Form className="space-y-8">
            {/* Información básica */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Información Básica
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Nombre de la empresa*"
                  name="companyName"
                  placeholder="Empresa ABC"
                  value={values.companyName}
                  onChange={e => setFieldValue('companyName', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.companyName && errors.companyName
                      ? errors.companyName
                      : undefined
                  }
                />
                <Input
                  label="RFC*"
                  name="rfc"
                  placeholder="FGT990101XXX"
                  value={values.rfc}
                  onChange={e =>
                    setFieldValue('rfc', e.target.value.toUpperCase())
                  }
                  onBlur={handleBlur}
                  error={touched.rfc && errors.rfc ? errors.rfc : undefined}
                  maxLength={13}
                  disabled
                />
                <Input
                  label="Razón Social*"
                  name="socialReason"
                  placeholder="Escribe aquí"
                  value={values.socialReason}
                  onChange={e => setFieldValue('socialReason', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.socialReason && errors.socialReason
                      ? errors.socialReason
                      : undefined
                  }
                  tooltip="Debe de ser igual a como se muestra en su Constancia"
                />
                <Input
                  label="Correo electrónico*"
                  name="email"
                  type="email"
                  placeholder="Escribe aquí"
                  value={values.email}
                  onChange={e => setFieldValue('email', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.email && errors.email ? errors.email : undefined
                  }
                  tooltip="Se utilizará para enviar un NIP para consultar su buró de crédito posteriormente"
                />
                <Input
                  label="Teléfono de la empresa*"
                  name="phone"
                  placeholder="Escribe aquí"
                  value={values.phone}
                  onChange={e =>
                    handlePhoneChange(e.target.value, setFieldValue)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.phone && errors.phone ? errors.phone : undefined
                  }
                  maxLength={10}
                />
                <Input
                  label="Sitio web (opcional)"
                  name="website"
                  placeholder="Escribe aquí"
                  value={values.website}
                  onChange={e => setFieldValue('website', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.website && errors.website
                      ? errors.website
                      : undefined
                  }
                />
              </div>
            </div>

            {/* Domicilio Fiscal */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Domicilio Fiscal
              </h3>

              {!postalCodeData && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <svg
                      className="inline-block w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Ingresa primero el código postal para autocompletar la
                    colonia, ciudad y estado
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Calle*"
                  name="street"
                  placeholder="Escribe aquí"
                  value={values.street}
                  onChange={e => setFieldValue('street', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.street && errors.street ? errors.street : undefined
                  }
                />
                <Input
                  label="Número exterior*"
                  name="exteriorNumber"
                  placeholder="Escribe aquí"
                  value={values.exteriorNumber}
                  onChange={e =>
                    setFieldValue('exteriorNumber', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.exteriorNumber && errors.exteriorNumber
                      ? errors.exteriorNumber
                      : undefined
                  }
                />
                <Input
                  label="Número interior"
                  name="interiorNumber"
                  placeholder="Escribe aquí"
                  value={values.interiorNumber}
                  onChange={e =>
                    setFieldValue('interiorNumber', e.target.value)
                  }
                />
                <Input
                  label="Entre calles"
                  name="betweenStreets"
                  placeholder="Escribe aquí"
                  value={values.betweenStreets}
                  onChange={e =>
                    setFieldValue('betweenStreets', e.target.value)
                  }
                />
                <div className="relative">
                  <Input
                    label="Código postal*"
                    name="postalCode"
                    placeholder="12345"
                    value={values.postalCode}
                    onChange={e =>
                      handlePostalCodeChange(e.target.value, setFieldValue)
                    }
                    maxLength={5}
                    pattern="\d{5}"
                    onBlur={handleBlur}
                    error={
                      touched.postalCode && errors.postalCode
                        ? errors.postalCode
                        : undefined
                    }
                  />
                  {loadingPostalCode && (
                    <div className="absolute right-3 top-9">
                      <svg
                        className="animate-spin h-4 w-4 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>

                {colonies.length > 0 ? (
                  <Select
                    label="Colonia*"
                    value={values.colony}
                    onValueChange={value => setFieldValue('colony', value)}
                    options={colonies}
                    placeholder="Selecciona una colonia"
                    onBlur={handleBlur}
                    error={
                      touched.colony && errors.colony
                        ? errors.colony
                        : undefined
                    }
                  />
                ) : (
                  <Input
                    label="Colonia*"
                    value={values.colony}
                    disabled
                    placeholder="Ingresa primero el código postal"
                    onBlur={handleBlur}
                    error={
                      touched.colony && errors.colony
                        ? errors.colony
                        : undefined
                    }
                  />
                )}

                <Input
                  label="Ciudad*"
                  value={values.city}
                  disabled={!postalCodeData}
                  placeholder={
                    !postalCodeData ? 'Se llenará automáticamente' : ''
                  }
                  onChange={e => setFieldValue('city', e.target.value)}
                  onBlur={handleBlur}
                  error={touched.city && errors.city ? errors.city : undefined}
                />

                <Input
                  label="Estado*"
                  value={values.state}
                  disabled={!postalCodeData}
                  placeholder={
                    !postalCodeData ? 'Se llenará automáticamente' : ''
                  }
                  onChange={e => setFieldValue('state', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.state && errors.state ? errors.state : undefined
                  }
                />
              </div>
            </div>

            {/* Domicilio Operativo */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Domicilio Operativo
              </h3>

              {/* Checkbox para mismo domicilio */}
              <div className="mb-6">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={values.operativeSameAsFiscalAddress}
                    onChange={e =>
                      handleSameAsFiscalChange(
                        e.target.checked,
                        values,
                        setFieldValue
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">
                    El Domicilio Operativo es el mismo del Domicilio Fiscal
                  </span>
                </label>
              </div>

              {!values.operativeSameAsFiscalAddress &&
                !operativePostalCodeData && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                      <svg
                        className="inline-block w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Ingresa primero el código postal para autocompletar la
                      colonia, ciudad y estado
                    </p>
                  </div>
                )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Calle*"
                  name="operativeStreet"
                  placeholder="Escribe aquí"
                  value={values.operativeStreet}
                  onChange={e =>
                    setFieldValue('operativeStreet', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.operativeStreet && errors.operativeStreet
                      ? errors.operativeStreet
                      : undefined
                  }
                  disabled={values.operativeSameAsFiscalAddress}
                />
                <Input
                  label="Número exterior*"
                  name="operativeExteriorNumber"
                  placeholder="Escribe aquí"
                  value={values.operativeExteriorNumber}
                  onChange={e =>
                    setFieldValue('operativeExteriorNumber', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.operativeExteriorNumber &&
                    errors.operativeExteriorNumber
                      ? errors.operativeExteriorNumber
                      : undefined
                  }
                  disabled={values.operativeSameAsFiscalAddress}
                />
                <Input
                  label="Número interior"
                  name="operativeInteriorNumber"
                  placeholder="Escribe aquí"
                  value={values.operativeInteriorNumber}
                  onChange={e =>
                    setFieldValue('operativeInteriorNumber', e.target.value)
                  }
                  disabled={values.operativeSameAsFiscalAddress}
                />
                <Input
                  label="Entre calles"
                  name="operativeBetweenStreets"
                  placeholder="Escribe aquí"
                  value={values.operativeBetweenStreets}
                  onChange={e =>
                    setFieldValue('operativeBetweenStreets', e.target.value)
                  }
                  disabled={values.operativeSameAsFiscalAddress}
                />
                <div className="relative">
                  <Input
                    label="Código postal*"
                    name="operativePostalCode"
                    placeholder="12345"
                    value={values.operativePostalCode}
                    onChange={e =>
                      handlePostalCodeChange(
                        e.target.value,
                        setFieldValue,
                        'operative'
                      )
                    }
                    onBlur={handleBlur}
                    maxLength={5}
                    pattern="\d{5}"
                    error={
                      touched.operativePostalCode && errors.operativePostalCode
                        ? errors.operativePostalCode
                        : undefined
                    }
                    disabled={values.operativeSameAsFiscalAddress}
                  />
                  {loadingOperativePostalCode && (
                    <div className="absolute right-3 top-9">
                      <svg
                        className="animate-spin h-4 w-4 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>

                {operativeColonies.length > 0 &&
                !values.operativeSameAsFiscalAddress ? (
                  <Select
                    label="Colonia*"
                    value={values.operativeColony}
                    onValueChange={value =>
                      setFieldValue('operativeColony', value)
                    }
                    onBlur={handleBlur}
                    options={operativeColonies}
                    placeholder="Selecciona una colonia"
                    error={
                      touched.operativeColony && errors.operativeColony
                        ? errors.operativeColony
                        : undefined
                    }
                  />
                ) : (
                  <Input
                    label="Colonia*"
                    value={values.operativeColony}
                    disabled={
                      values.operativeSameAsFiscalAddress ||
                      !operativePostalCodeData
                    }
                    onBlur={handleBlur}
                    placeholder={
                      values.operativeSameAsFiscalAddress
                        ? 'Copiado del domicilio fiscal'
                        : 'Ingresa primero el código postal'
                    }
                    error={
                      touched.operativeColony && errors.operativeColony
                        ? errors.operativeColony
                        : undefined
                    }
                  />
                )}

                <Input
                  label="Ciudad*"
                  value={values.operativeCity}
                  disabled={
                    values.operativeSameAsFiscalAddress ||
                    !operativePostalCodeData
                  }
                  placeholder={
                    values.operativeSameAsFiscalAddress
                      ? 'Copiado del domicilio fiscal'
                      : !operativePostalCodeData
                        ? 'Se llenará automáticamente'
                        : ''
                  }
                  onChange={e => setFieldValue('operativeCity', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.operativeCity && errors.operativeCity
                      ? errors.operativeCity
                      : undefined
                  }
                />

                <Input
                  label="Estado*"
                  value={values.operativeState}
                  disabled={
                    values.operativeSameAsFiscalAddress ||
                    !operativePostalCodeData
                  }
                  placeholder={
                    values.operativeSameAsFiscalAddress
                      ? 'Copiado del domicilio fiscal'
                      : !operativePostalCodeData
                        ? 'Se llenará automáticamente'
                        : ''
                  }
                  onChange={e =>
                    setFieldValue('operativeState', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.operativeState && errors.operativeState
                      ? errors.operativeState
                      : undefined
                  }
                />
              </div>
            </div>

            {/* Campos obligatorios */}
            <div className="pt-4">
              <p className="text-sm text-gray-600">*Campos obligatorios</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                className="px-12 py-3 min-w-[230px] sm:order-2"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Continuar'}
              </Button>

              <div className="sm:order-1">
                <ContinueLaterModal />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CompanyDataStep
